/**
 * Pieces of the world saved to be put down again.
 *
 * A village is a hut repeated. Building the second hut by hand is how the
 * second hut ends up subtly different from the first, and building the tenth is
 * how an afternoon goes. Draw a square round something worth keeping, name it,
 * and it can be stamped anywhere afterwards at any of the four turns.
 *
 * What is saved is read out of the world by the server rather than sent up from
 * here: the world is the thing being saved, so the world is what is asked.
 */

import { useEffect, useState } from 'react';
import type { AdminCommand } from '../Admin';

export interface SavedGroup {
  name: string;
  pieces: number;
  width: number;
  height: number;
}

/** The square currently drawn in the world, which is what a save takes. */
export interface GroupArea {
  tx: number;
  tz: number;
  width: number;
  height: number;
}

export function EditorGroups({
  groups,
  area,
  onCommand,
}: {
  groups: readonly SavedGroup[];
  /** The ground held in the world, or nothing when nothing is held. */
  area: GroupArea | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [turns, setTurns] = useState(0);

  useEffect(() => onCommand('listGroups'), [onCommand]);

  const held = groups.find((group) => group.name === chosen) ?? null;

  const save = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!area || name.trim().length === 0) return;

    onCommand(
      'saveGroup',
      { tx: area.tx, tz: area.tz, width: area.width, height: area.height },
      { name: name.trim() },
    );
    setName('');

    // The list comes back from the world rather than being guessed at here.
    window.setTimeout(() => onCommand('listGroups'), 400);
  };

  return (
    <>
      <h3>Groups</h3>
      <p className="hint">
        Draw a square round something and save it. It can be put down anywhere afterwards.
      </p>

      <form className="editor-brushes" onSubmit={save}>
        <input
          type="text"
          className="editor-search"
          value={name}
          maxLength={40}
          placeholder="Name this square"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" className="editor-tab" disabled={!area || name.trim().length === 0}>
          Save
        </button>
      </form>

      <p className="hint">
        {area
          ? `Holding ${area.width} by ${area.height} at ${area.tx}, ${area.tz}.`
          : 'Nothing held. Drag out a square in the world first.'}
      </p>

      <div className="editor-list">
        {groups.map((group) => (
          <button
            key={group.name}
            type="button"
            className={group.name === chosen ? 'editor-row on' : 'editor-row'}
            onClick={() => setChosen(group.name)}
          >
            <span>{group.name}</span>
            <span className="editor-row-id">
              {group.pieces} in {group.width}×{group.height}
            </span>
          </button>
        ))}

        {groups.length === 0 ? <p className="hint">Nothing saved yet.</p> : null}
      </div>

      {held ? (
        <>
          <h4>{held.name}</h4>

          <label>
            Turn
            <select value={turns} onChange={(event) => setTurns(Number(event.target.value))}>
              <option value={0}>As saved</option>
              <option value={1}>A quarter</option>
              <option value={2}>A half</option>
              <option value={3}>Three quarters</option>
            </select>
          </label>

          <div className="editor-brushes">
            <button
              type="button"
              className="editor-tab"
              disabled={!area}
              title={
                area
                  ? 'Put it down in the middle of what is held'
                  : 'Hold some ground in the world first'
              }
              onClick={() => {
                if (!area) return;

                // The middle of what is held, since that is where the middle of
                // the group was taken from.
                onCommand(
                  'placeGroup',
                  {
                    tx: area.tx + Math.floor(area.width / 2),
                    tz: area.tz + Math.floor(area.height / 2),
                    turns,
                  },
                  { name: held.name },
                );
              }}
            >
              Put It Down
            </button>

            <button
              type="button"
              className="editor-tab danger"
              onClick={() => {
                onCommand('removeGroup', {}, { name: held.name });
                setChosen(null);
                window.setTimeout(() => onCommand('listGroups'), 400);
              }}
            >
              Take Out
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}
